import { ChangeEvent, useState } from 'react'
import { useAuth } from '@/lib/auth-context'
import {
  Pager,
  Section,
  TableScroller,
  ToolbarInput,
  ToolbarSelect,
  useBearer,
  useDebouncedValue,
  usePagedQuery,
} from './_admin-utils'
import { inviteSponsorRequest } from './_sponsor-invite'

interface SponsorRequest {
  id: string
  email: string
  org_name?: string
  contact_name?: string
  requested_at?: string
  status?: string
  onboarding_status?: string
  action_link?: string
}

interface FetcherParams {
  page: number
  pageSize: number
}

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'requested', label: 'Requested' },
  { value: 'link_sent', label: 'Link Sent' },
  { value: 'fulfilled', label: 'Fulfilled' },
]

const ONBOARDING_OPTIONS = [
  { value: '', label: 'All onboarding' },
  { value: 'pending', label: 'Pending' },
  { value: 'released', label: 'Released' },
]

export default function Sponsors() {
  const { profile } = useAuth()
  const bearer = useBearer()
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query.trim(), 300)
  const [status, setStatus] = useState('')
  const [onboardingStatus, setOnboardingStatus] = useState('')
  const [releasingId, setReleasingId] = useState<string | null>(null)
  const [invitingId, setInvitingId] = useState<string | null>(null)
  const [copyMessage, setCopyMessage] = useState<string | null>(null)
  const [releaseErrors, setReleaseErrors] = useState<Record<string, string | null>>({})

  const fetcher = async ({ page, pageSize }: FetcherParams): Promise<{ items: SponsorRequest[]; total: number }> => {
    if (!bearer) throw new Error('Not authenticated')
    const params = new URLSearchParams()
    if (debouncedQuery) params.set('q', debouncedQuery)
    if (status) params.set('status', status)
    if (onboardingStatus) params.set('onboarding_status', onboardingStatus)
    params.set('page', String(page))
    params.set('pageSize', String(pageSize))

    const response = await fetch(`/api/v1/admin/sponsor-requests?${params.toString()}`, {
      headers: { ...bearer },
    })
    const json = await response.json().catch(() => ({})) as { data?: { items?: SponsorRequest[]; total?: number }; items?: SponsorRequest[]; total?: number; error?: string }
    if (!response.ok) {
      throw new Error(json?.error || 'Failed to load sponsors')
    }
    return {
      items: Array.isArray(json?.data?.items) ? json.data.items : Array.isArray(json?.items) ? json.items : [],
      total: typeof json?.data?.total === 'number' ? json.data.total : typeof json?.total === 'number' ? json.total : 0,
    }
  }

  const { items, page, pageSize, total, pageCount, loading, error, load } = usePagedQuery<SponsorRequest>(fetcher, [
    debouncedQuery,
    status,
    onboardingStatus,
  ])

  const copyInviteToClipboard = async (subject: string, body: string): Promise<boolean> => {
    if (typeof navigator === 'undefined' || !navigator?.clipboard) return false
    try {
      await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`)
      return true
    } catch {
      return false
    }
  }

  const handleInvite = async (item: SponsorRequest) => {
    if (!bearer) {
      setCopyMessage('Not authenticated')
      setTimeout(() => setCopyMessage(null), 2500)
      return
    }
    const sendNow = window.confirm(
      'Send the sponsor invite email now from Team Tower? Click OK to send, Cancel to only copy.'
    )
    setInvitingId(item.id)
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : ''
      const out = await inviteSponsorRequest(bearer, item.email, {
        org_name: item.org_name,
        checkout_url: `${origin}/checkout`,
        onboarding_url: `${origin}/onboarding`,
        redirect_to: `${origin}/login`,
        send_now: !!sendNow,
      })
      if (out.sent) {
        setCopyMessage('Invite emailed')
      } else {
        const copied = await copyInviteToClipboard(out.subject || '', out.body || '')
        setCopyMessage(copied ? 'Invite email copied to clipboard' : 'Invite ready — copy from response')
      }
      setTimeout(() => setCopyMessage(null), 2500)
      await load(page)
    } catch (err: unknown) {
      setCopyMessage((err as Error | null)?.message || 'Failed to prepare invite')
      setTimeout(() => setCopyMessage(null), 2500)
    } finally {
      setInvitingId(null)
    }
  }

  const isAdmin = ['admin', 'superadmin'].includes(profile?.role || '')
  if (!isAdmin) {
    return <div className="max-w-3xl mx-auto px-4 py-10 text-sm text-cocoa/70">You do not have permission to view this page.</div>
  }

  const handlePageChange = (nextPage: number) => {
    if (loading || nextPage === page) return
    load(nextPage)
  }

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const next = Number.parseInt(event.target.value, 10)
    const value = Number.isFinite(next) ? next : pageSize
    load(1, value)
  }

  const handleRelease = async (id: string) => {
    if (!bearer) return
    setReleasingId(id)
    setReleaseErrors((prev) => ({ ...prev, [id]: null }))
    try {
      const response = await fetch(`/api/v1/admin/sponsors/${id}/release`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...bearer,
        },
        body: JSON.stringify({ id }),
      })

      const json = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(json?.error || 'Failed to release packet')
      }

      await load(page)
      const actionLink = json?.action_link
      if (actionLink && typeof navigator !== 'undefined') {
        await navigator.clipboard?.writeText(actionLink).catch(() => {})
        setCopyMessage('Magic link copied to clipboard')
        setTimeout(() => setCopyMessage(null), 2500)
      }
    } catch (err: unknown) {
      setReleaseErrors((prev) => ({ ...prev, [id]: (err as Error | null)?.message || 'Failed to release packet' }))
    } finally {
      setReleasingId(null)
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 space-y-6">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold text-cocoa">Sponsor Requests</h1>
        <p className="text-sm text-cocoa/70">
          Review sponsor interest, track onboarding progress, and release welcome packets when classrooms are ready.
        </p>
      </header>

      <Section
        title="Requests"
        actions={
          <div className="grid gap-3 md:grid-cols-[minmax(0,1.5fr)_200px_200px_auto] md:items-center">
            <div className="md:col-span-1">
              <ToolbarInput value={query} onChange={setQuery} placeholder="Search by email, organization, or contact" />
            </div>
            <ToolbarSelect value={status} onChange={setStatus} options={STATUS_OPTIONS} />
            <ToolbarSelect value={onboardingStatus} onChange={setOnboardingStatus} options={ONBOARDING_OPTIONS} />
            <a href="/portal/admin" className="text-sm underline text-cocoa/70 hover:text-cocoa">
              ← Admin
            </a>
          </div>
        }
      >
        {copyMessage ? (
          <div className="rounded-lg border border-mint/40 bg-mint/10 px-3 py-2 text-sm text-cocoa/80">{copyMessage}</div>
        ) : null}

        <div className="overflow-hidden rounded-xl border border-white/70">
          <TableScroller minWidth={760}>
            <table className="min-w-full divide-y divide-white/60 text-sm whitespace-nowrap">
              <thead className="bg-shell/60 text-cocoa/70 sticky top-0">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Organization</th>
                <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Contact</th>
                <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Status</th>
                <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Actions</th>
              </tr>
              </thead>
              <tbody className="divide-y divide-white/60 bg-white/50">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-cocoa/60">
                    Loading sponsor requests…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-cocoa/60">
                    {debouncedQuery || status || onboardingStatus
                      ? 'No sponsor requests match your filters yet.'
                      : 'No sponsor requests yet.'}
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const errorMessage = releaseErrors[item.id]
                  return (
                    <tr key={item.id} className="align-top hover:bg-shell/40 transition-colors">
                      <td className="px-4 py-3 space-y-1 text-cocoa/90">
                        <div className="font-semibold">{item.org_name || '—'}</div>
                        <div className="text-xs text-cocoa/70 font-mono">{item.email}</div>
                      </td>
                      <td className="px-4 py-3 space-y-1 text-cocoa/80">
                        <div>{item.contact_name || '—'}</div>
                        <div className="text-xs text-cocoa/60">
                          Requested {item.requested_at ? new Date(item.requested_at).toLocaleString() : '—'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-cocoa/80">
                        <div className="text-xs uppercase tracking-wide text-cocoa/60">Status</div>
                        <div className="font-medium">{item.status}</div>
                        <div className="mt-2 text-xs uppercase tracking-wide text-cocoa/60">Onboarding</div>
                        <div className="font-medium">{item.onboarding_status}</div>
                      </td>
                      <td className="px-4 py-3 space-y-3">
                        <button
                          type="button"
                          onClick={() => handleRelease(item.id)}
                          disabled={releasingId === item.id}
                          className="rounded-full bg-terracotta px-4 py-2 text-sm font-semibold text-white shadow disabled:opacity-60"
                        >
                          {releasingId === item.id ? 'Releasing…' : 'Release Packet'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleInvite(item)}
                          disabled={invitingId === item.id}
                          className="rounded-full border border-shell px-4 py-2 text-sm font-semibold text-cocoa hover:bg-shell/30 disabled:opacity-60"
                        >
                          {invitingId === item.id ? 'Preparing…' : 'Invite by Email'}
                        </button>
                        {errorMessage ? <div className="text-xs text-red-600">{errorMessage}</div> : null}
                        {item.action_link ? (
                          <div className="space-y-1">
                            <div className="text-xs font-semibold uppercase tracking-wide text-cocoa/60">Sponsor link</div>
                            <input
                              type="text"
                              readOnly
                              value={item.action_link}
                              className="w-full rounded-md border border-white/70 bg-white px-2 py-1 text-xs font-mono"
                              onFocus={(event) => event.target.select()}
                            />
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  )
                })
              )}
              </tbody>
            </table>
          </TableScroller>
        </div>

        {error ? <div className="text-sm text-red-600">Error: {String(error)}</div> : null}

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between text-sm text-cocoa/70">
          <div>
            Showing page {page} of {pageCount} · {total} sponsor requests
          </div>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
            <Pager page={page} pageCount={pageCount} onPage={handlePageChange} />
            <label className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wide text-cocoa/60">Page size</span>
              <select
                className="rounded-lg border border-white/70 bg-shell/40 px-2 py-1 text-sm text-cocoa focus:border-terracotta focus:outline-none"
                value={pageSize}
                onChange={handlePageSizeChange}
              >
                {[10, 20, 50, 100].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </Section>
    </div>
  )
}
